import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { 
  Calendar, 
  Plus, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  Edit2,
  Trash2,
  X,
  Filter,
  Bell,
  Globe,
  Sparkles,
  RotateCcw,
  List,
  CalendarDays,
  Repeat
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import EnrichmentPopup from "@/components/EnrichmentPopup";
import DeadlineCalendar from "@/components/DeadlineCalendar";

const PRIORITY_COLORS = {
  high: "bg-red-500/15 text-red-400 border-red-500/20",
  medium: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  low: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20"
};

const CATEGORIES = ["PPWR", "Packaging EPR", "WEEE", "Battery", "Custom"];

export default function Deadlines() {
  const api = useApi();
  const [deadlines, setDeadlines] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [countryFilter, setCountryFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [countries, setCountries] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  
  // Enrichment state
  const [enrichmentOpen, setEnrichmentOpen] = useState(false);
  const [enrichmentField, setEnrichmentField] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    country_code: "",
    category: "Custom",
    priority: "medium",
    is_recurring: false,
    recurrence_type: "annual",
    recurrence_pattern: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [deadlinesRes, upcomingRes, countriesRes] = await Promise.all([
        api.get("/api/deadlines/"),
        api.get("/api/deadlines/upcoming?days_ahead=365"),
        api.get("/api/countries/")
      ]);
      setDeadlines(deadlinesRes.data?.deadlines || []);
      setUpcomingDeadlines(upcomingRes.data?.deadlines || []);
      setCountries(countriesRes.data?.countries || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load deadlines");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate based on whether it's recurring or not
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    
    if (formData.is_recurring) {
      if (!formData.recurrence_pattern) {
        toast.error("Recurrence pattern is required for recurring deadlines");
        return;
      }
    } else {
      if (!formData.date) {
        toast.error("Date is required for non-recurring deadlines");
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        date: formData.is_recurring ? null : formData.date
      };
      
      if (editingDeadline) {
        await api.put(`/api/deadlines/${editingDeadline.id}`, payload);
        toast.success("Deadline updated");
      } else {
        await api.post("/api/deadlines/", payload);
        toast.success("Deadline created");
      }
      setShowAddDialog(false);
      setEditingDeadline(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving deadline:", error);
      toast.error(error.response?.data?.detail || "Failed to save deadline");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this deadline?")) return;
    
    try {
      await api.delete(`/api/deadlines/${id}`);
      toast.success("Deadline deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting deadline:", error);
      toast.error("Failed to delete deadline");
    }
  };

  const handleToggleComplete = async (deadline) => {
    try {
      await api.patch(`/api/deadlines/${deadline.id}/complete`, { completed: !deadline.completed });
      toast.success(deadline.completed ? "Marked as incomplete" : "Marked as complete");
      fetchData();
    } catch (error) {
      console.error("Error toggling complete:", error);
      toast.error("Failed to update deadline");
    }
  };

  const handleReset = async (deadline) => {
    if (!window.confirm("Reset this deadline to its original values?")) return;
    
    try {
      await api.post(`/api/deadlines/${deadline.id}/reset`);
      toast.success("Deadline reset to original");
      fetchData();
    } catch (error) {
      console.error("Error resetting deadline:", error);
      toast.error("Failed to reset deadline");
    }
  };

  const openEnrichment = (fieldName, fieldType, currentValue) => {
    setEnrichmentField({ fieldName, fieldType, currentValue });
    setEnrichmentOpen(true);
  };

  const handleEnrichmentAccept = (value) => {
    if (enrichmentField) {
      setFormData(prev => ({
        ...prev,
        [enrichmentField.fieldName]: value
      }));
    }
    setEnrichmentOpen(false);
  };

  const openEditDialog = (deadline) => {
    setFormData({
      title: deadline.title,
      description: deadline.description || "",
      date: deadline.date || "",
      country_code: deadline.country_code || "",
      category: deadline.category || "Custom",
      priority: deadline.priority || "medium",
      is_recurring: deadline.is_recurring || false,
      recurrence_type: deadline.recurrence_type || "annual",
      recurrence_pattern: deadline.recurrence_pattern || ""
    });
    setEditingDeadline(deadline);
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      date: "",
      country_code: "",
      category: "Custom",
      priority: "medium",
      is_recurring: false,
      recurrence_type: "annual",
      recurrence_pattern: ""
    });
  };

  const filteredDeadlines = deadlines.filter(d => {
    if (countryFilter !== "all" && d.country_code !== countryFilter && d.country_code !== "EU") return false;
    if (categoryFilter !== "all" && d.category !== categoryFilter) return false;
    return true;
  });

  const uniqueCountryCodes = [...new Set(deadlines.map(d => d.country_code).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="deadlines-page">
      <div className="page-header compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Compliance Deadlines</h1>
            <p className="page-subtitle">
              Track regulatory deadlines across all markets
            </p>
          </div>
          <Button size="sm" onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-deadline-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Deadline
          </Button>
        </div>
      </div>

      {/* Upcoming Deadlines Summary - Compact */}
      {upcomingDeadlines.length > 0 && (
        <div className="card p-3 mb-4 bg-gradient-to-r from-amber-500/10 to-red-500/10 border-amber-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-amber-500" />
            <h3 className="font-semibold text-gray-900 text-sm">Upcoming Deadlines</h3>
            <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-500">{upcomingDeadlines.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {upcomingDeadlines.slice(0, 6).map(deadline => (
              <div 
                key={deadline.id} 
                className="bg-white/80 rounded-lg p-2 border border-gray-200"
                data-testid={`upcoming-${deadline.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-xs text-gray-900 truncate">{deadline.title}</p>
                    <p className="text-[10px] text-gray-500">{deadline.date}</p>
                  </div>
                  <Badge className={`text-[10px] ml-1.5 ${PRIORITY_COLORS[deadline.priority] || PRIORITY_COLORS.medium}`}>
                    {deadline.days_until}d
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters - Compact */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap gap-2">
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[150px] h-8 text-xs bg-gray-50 border-gray-200 text-gray-900" data-testid="country-filter">
              <Globe className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="All Countries" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              <SelectItem value="all">All Countries</SelectItem>
              <SelectItem value="EU">EU-wide</SelectItem>
              {uniqueCountryCodes.filter(c => c !== "EU").map(code => (
                <SelectItem key={code} value={code}>{code}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[150px] h-8 text-xs bg-gray-50 border-gray-200 text-gray-900" data-testid="category-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-white rounded-lg p-0.5 border border-gray-200">
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className={`h-7 px-3 text-xs ${viewMode === "list" ? "bg-violet-500 text-white" : "text-gray-500 hover:text-gray-900"}`}
            onClick={() => setViewMode("list")}
            data-testid="view-list-btn"
          >
            <List className="w-3.5 h-3.5 mr-1.5" />
            List
          </Button>
          <Button
            variant={viewMode === "calendar" ? "secondary" : "ghost"}
            size="sm"
            className={`h-7 px-3 text-xs ${viewMode === "calendar" ? "bg-violet-500 text-white" : "text-gray-500 hover:text-gray-900"}`}
            onClick={() => setViewMode("calendar")}
            data-testid="view-calendar-btn"
          >
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
            Calendar
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <DeadlineCalendar 
          deadlines={filteredDeadlines}
          onEditDeadline={openEditDialog}
        />
      )}

      {/* List View with Tabs */}
      {viewMode === "list" && (
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-4 h-8">
            <TabsTrigger value="all" className="text-xs" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="prepopulated" className="text-xs" data-testid="tab-prepopulated">Regulatory</TabsTrigger>
            <TabsTrigger value="custom" className="text-xs" data-testid="tab-custom">Custom</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <DeadlinesList 
              deadlines={filteredDeadlines} 
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onToggleComplete={handleToggleComplete}
              onReset={handleReset}
            />
          </TabsContent>

          <TabsContent value="prepopulated">
            <DeadlinesList 
              deadlines={filteredDeadlines.filter(d => !d.is_custom)} 
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onToggleComplete={handleToggleComplete}
              onReset={handleReset}
            />
          </TabsContent>

          <TabsContent value="custom">
            <DeadlinesList 
              deadlines={filteredDeadlines.filter(d => d.is_custom)} 
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onToggleComplete={handleToggleComplete}
              onReset={handleReset}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingDeadline ? "Edit Deadline" : "Add Custom Deadline"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="title" className="text-gray-600">Title *</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-violet-500 hover:text-violet-400 hover:bg-violet-500/10"
                  onClick={() => openEnrichment('title', 'text', formData.title)}
                  data-testid="enrich-title-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  <span className="text-xs">AI</span>
                </Button>
              </div>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="E.g., Germany Q1 Report"
                data-testid="deadline-title-input"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>

            {/* Recurring Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-violet-500" />
                <div>
                  <Label htmlFor="recurring" className="text-sm font-medium text-gray-900">Recurring Deadline</Label>
                  <p className="text-xs text-gray-500">Repeats automatically each year/quarter/month</p>
                </div>
              </div>
              <Switch
                id="recurring"
                checked={formData.is_recurring}
                onCheckedChange={(checked) => setFormData({...formData, is_recurring: checked})}
                data-testid="recurring-switch"
              />
            </div>

            {/* Recurrence Options - shown when recurring is enabled */}
            {formData.is_recurring ? (
              <div className="space-y-4 p-3 border border-violet-200 rounded-lg bg-violet-50/50">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Recurrence Type</Label>
                    <Select 
                      value={formData.recurrence_type} 
                      onValueChange={(v) => setFormData({...formData, recurrence_type: v, recurrence_pattern: ""})}
                    >
                      <SelectTrigger data-testid="recurrence-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="annual">Annual (Yearly)</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>
                      {formData.recurrence_type === "annual" ? "Date (MM-DD)" : "Day of Month"}
                    </Label>
                    <Input
                      value={formData.recurrence_pattern}
                      onChange={(e) => setFormData({...formData, recurrence_pattern: e.target.value})}
                      placeholder={formData.recurrence_type === "annual" ? "e.g., 03-15" : "e.g., 15"}
                      data-testid="recurrence-pattern-input"
                    />
                    <p className="text-[10px] text-zinc-500">
                      {formData.recurrence_type === "annual" 
                        ? "Format: MM-DD (e.g., 03-15 for March 15)"
                        : formData.recurrence_type === "quarterly"
                        ? "Day of quarter end month (Mar, Jun, Sep, Dec)"
                        : "Day of each month (1-31)"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  data-testid="deadline-date-input"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description">Description</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-violet-500 hover:text-violet-600"
                  onClick={() => openEnrichment('description', 'text', formData.description)}
                  data-testid="enrich-description-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  <span className="text-xs">AI</span>
                </Button>
              </div>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select value={formData.country_code} onValueChange={(v) => setFormData({...formData, country_code: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EU">EU-wide</SelectItem>
                    {countries.map(c => (
                      <SelectItem key={c.country_code} value={c.country_code}>
                        {c.flag_emoji} {c.country_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-deadline-btn">
              {editingDeadline ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Enrichment Popup */}
      <EnrichmentPopup
        isOpen={enrichmentOpen}
        onClose={() => setEnrichmentOpen(false)}
        fieldType={enrichmentField?.fieldType || 'text'}
        fieldName={enrichmentField?.fieldName || ''}
        currentValue={enrichmentField?.currentValue}
        countryCode={formData.country_code}
        category={formData.category}
        onAccept={handleEnrichmentAccept}
      />
    </div>
  );
}

function DeadlinesList({ deadlines, onEdit, onDelete, onToggleComplete, onReset }) {
  if (deadlines.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-gray-900 mb-1">No Deadlines Found</h3>
        <p className="text-xs text-gray-500">Add custom deadlines or adjust your filters</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {deadlines.map(deadline => (
        <div 
          key={deadline.id} 
          className={`card p-3 ${deadline.completed ? 'opacity-60' : ''}`}
          data-testid={`deadline-item-${deadline.id}`}
        >
          <div className="flex items-start gap-3">
            {/* Checkbox for all deadlines */}
            <button 
              onClick={() => onToggleComplete(deadline)}
              className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                deadline.completed 
                  ? 'bg-emerald-500 border-emerald-500 text-white' 
                  : 'border-gray-200 hover:border-violet-500'
              }`}
              data-testid={`toggle-complete-${deadline.id}`}
            >
              {deadline.completed && <CheckCircle2 className="w-2.5 h-2.5" />}
            </button>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className={`font-medium text-gray-900 text-sm ${deadline.completed ? 'line-through' : ''}`}>
                    {deadline.title}
                  </h4>
                  {deadline.description && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{deadline.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                  <Badge className={`text-[10px] ${PRIORITY_COLORS[deadline.priority] || PRIORITY_COLORS.medium}`}>
                    {deadline.priority}
                  </Badge>
                  {deadline.is_custom ? (
                    <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">Custom</Badge>
                  ) : deadline.has_override ? (
                    <Badge className="text-[10px] bg-amber-500/15 text-amber-500">Modified</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px] bg-white/5 text-gray-500">Regulatory</Badge>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                {deadline.date ? (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {deadline.date}
                  </span>
                ) : deadline.deadline_text ? (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {deadline.deadline_text}
                  </span>
                ) : null}
                
                {deadline.is_recurring && (
                  <span className="flex items-center gap-1 text-violet-500">
                    <Repeat className="w-3 h-3" />
                    {deadline.recurrence_type === "annual" ? "Annual" : 
                     deadline.recurrence_type === "quarterly" ? "Quarterly" : "Monthly"}
                  </span>
                )}
                
                {deadline.country_code && (
                  <span className="flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {deadline.country_code}
                  </span>
                )}
                
                <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">{deadline.category}</Badge>
              </div>
            </div>
            
            {/* Actions for all deadlines */}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="sm"
                className="h-6 w-6 p-0 text-gray-500 hover:text-gray-900 hover:bg-white/10"
                onClick={() => onEdit(deadline)}
                data-testid={`edit-deadline-${deadline.id}`}
              >
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              {deadline.has_override && !deadline.is_custom && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-6 w-6 p-0 text-amber-500 hover:text-amber-500/80 hover:bg-amber-500/10"
                  onClick={() => onReset(deadline)}
                  title="Reset to original"
                  data-testid={`reset-deadline-${deadline.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
              {deadline.is_custom && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={() => onDelete(deadline.id)}
                  data-testid={`delete-deadline-${deadline.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
